import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

type Theme = 'light' | 'dark';

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const applyTheme = (theme: Theme) => {
  if (typeof document === 'undefined') return;
  
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => {
      // Função auxiliar para aplicar tema e atualizar estado
      const updateTheme = (theme: Theme) => {
        // Aplicar tema imediatamente antes de atualizar o estado
        applyTheme(theme);
        // Atualizar estado (isso vai persistir no localStorage via persist middleware)
        set({ theme });
      };

      return {
        theme: 'light',
        setTheme: (theme: Theme) => {
          updateTheme(theme);
        },
        toggleTheme: () => {
          const currentTheme = get().theme;
          const newTheme = currentTheme === 'light' ? 'dark' : 'light';
          updateTheme(newTheme);
        },
      };
    },
    {
      name: 'theme-storage',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.error('Error rehydrating theme:', error);
          return;
        }
        
        // Aplicar tema ao hidratar após o estado ser restaurado
        if (state?.theme) {
          applyTheme(state.theme);
        }
      },
    }
  )
);

// Aplicar tema imediatamente ao carregar o módulo (antes do React renderizar)
if (typeof window !== 'undefined') {
  try {
    const stored = localStorage.getItem('theme-storage');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.state?.theme) {
        applyTheme(parsed.state.theme);
      }
    }
  } catch (e) {
    // Ignore errors
  }
}